import { create } from 'zustand';

/** GET /nodes のレスポンス1ノード分 (apps/api/src/routes/nodes.ts のレスポンス shape と対応) */
export type NodeTreeItem = {
  node_id: string;
  node_code: string;
  node_type_code: string;
  label: string;
  icon: string | null;
  name: string;
  status: string;
  parent_node_id: string | null;
  // v0.3 業態初期表示自動選択 (migration 0033)
  industry_template_id: string | null;
  depth_level: number;
  // v0.4 追加7要件⑦ (D-237): 店舗間移動時間の haversine 概算に使う緯度経度
  latitude: number | null;
  longitude: number | null;
  children: NodeTreeItem[];
};

export type NodesResponse = {
  roots: NodeTreeItem[];
  hierarchy_management_enabled: boolean;
};

const DEFAULT_ROOT_LABEL = '全社';
/** NodeSwitcher の「最近使用」に表示する上限件数 (docs/design/v025-config-ui.md §2-3 MVP要件、簡易実装) */
const MAX_RECENT_NODES = 3;

/** localStorage key はテナント別 namespace (StoreMultiSelect の savedViewsKey パターンを踏襲) */
const scopeStorageKey = (tenantId: string) => `misespark:scope-node-id:${tenantId}`;
const recentNodesStorageKey = (tenantId: string) => `misespark:scope-recent-node-ids:${tenantId}`;

const loadPersistedNodeId = (tenantId: string): string | null => {
  try {
    return localStorage.getItem(scopeStorageKey(tenantId));
  } catch {
    return null; // Private Mode / QuotaExceeded
  }
};

const persistNodeId = (tenantId: string, nodeId: string | null) => {
  try {
    if (nodeId) localStorage.setItem(scopeStorageKey(tenantId), nodeId);
    else localStorage.removeItem(scopeStorageKey(tenantId));
  } catch {
    // 保存できなくても選択状態はメモリ上の state には反映済みなので致命的ではない
  }
};

const loadRecentNodeIds = (tenantId: string): string[] => {
  try {
    const raw = localStorage.getItem(recentNodesStorageKey(tenantId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return []; // Private Mode / QuotaExceeded / 破損JSON
  }
};

const persistRecentNodeIds = (tenantId: string, nodeIds: string[]) => {
  try {
    localStorage.setItem(recentNodesStorageKey(tenantId), JSON.stringify(nodeIds));
  } catch {
    // 保存できなくても選択状態はメモリ上の state には反映済みなので致命的ではない
  }
};

type ScopeState = {
  /** 現在選択中のスコープノード。null はルート (=全社) を意味する */
  selectedNodeId: string | null;
  /** GET /nodes のレスポンス (森。渡来武のように会社/ブランドノード未構築のテナントでは
   *  店舗ごとに独立したルートが複数返ることがある) */
  roots: NodeTreeItem[];
  hierarchyManagementEnabled: boolean;
  /** setTree 時に localStorage 参照するため、直近の tenantId を保持 */
  tenantId: string | null;
  /** 直近選択した node_id (新しい順、最大 MAX_RECENT_NODES 件)。NodeSwitcher パネルの
   *  「最近使用」表示に使う (ピン留めは対象外、docs/design/v025-config-ui.md §2-3 の簡易実装) */
  recentNodeIds: string[];
  /** NodeSwitcher が GET /nodes 取得後に呼ぶ。tenant別に永続化された selectedNodeId も併せて復元する */
  setTree: (tenantId: string, data: NodesResponse) => void;
  setSelectedNodeId: (tenantId: string, nodeId: string | null) => void;
};

export const useScopeStore = create<ScopeState>((set) => ({
  selectedNodeId: null,
  roots: [],
  hierarchyManagementEnabled: false,
  tenantId: null,
  recentNodeIds: [],
  setTree: (tenantId, data) => set((s) => ({
    roots: data.roots,
    hierarchyManagementEnabled: data.hierarchy_management_enabled,
    tenantId,
    // tenant が切り替わった場合のみ永続化済みの選択ノード/最近使用を読み直す (同一テナント内の
    // 再取得でユーザーの選択中スコープを勝手に巻き戻さないため)
    selectedNodeId: s.tenantId === tenantId ? s.selectedNodeId : loadPersistedNodeId(tenantId),
    recentNodeIds: s.tenantId === tenantId ? s.recentNodeIds : loadRecentNodeIds(tenantId),
  })),
  setSelectedNodeId: (tenantId, nodeId) => {
    persistNodeId(tenantId, nodeId);
    set((s) => {
      if (!nodeId) return { selectedNodeId: null };
      const recentNodeIds = [nodeId, ...s.recentNodeIds.filter((id) => id !== nodeId)].slice(0, MAX_RECENT_NODES);
      persistRecentNodeIds(tenantId, recentNodeIds);
      return { selectedNodeId: nodeId, recentNodeIds };
    });
  },
}));

/** 指定ノードIDをツリーから検索する (深さ優先) */
export const findNodeInTree = (roots: NodeTreeItem[], nodeId: string): NodeTreeItem | null => {
  for (const node of roots) {
    if (node.node_id === nodeId) return node;
    const hit = findNodeInTree(node.children, nodeId);
    if (hit) return hit;
  }
  return null;
};

/** roots からルートラベルを算出する純粋関数。tree 未取得、または backfill 直後で店舗ごとに
 *  独立したルートが複数存在する (会社/ブランドノード未構築) 場合は、単一の「全社」という
 *  概念が定まらないため従来通り固定文言「全社」にフォールバックする
 *  (docs/design/v025-config-ui.md §0.6 Design-C1 対応)。
 *  useRootLabel から分離しているのは、React hook を経由せず単体テストできるようにするため。 */
export const computeRootLabel = (roots: NodeTreeItem[]): string =>
  roots.length === 1 ? roots[0]!.label : DEFAULT_ROOT_LABEL;

/** ルートノードの label を返す selector。詳細は computeRootLabel 参照。
 *  NodeSwitcher.tsx でも同一ロジックを重複実装しないよう、この selector を参照すること。 */
export const useRootLabel = (): string =>
  useScopeStore((s) => computeRootLabel(s.roots));
